import { motion } from "framer-motion";
import { MapPin, Phone, Mail, User } from "lucide-react";
import InquiryForm from "@/components/InquiryForm";
import { useSettings } from "@/context/SettingsContext";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function Contact() {
  const { settings } = useSettings();
  const contact = settings?.contact || {};
  const admissionContacts = settings?.admissionContacts || [];
  const brand = settings?.branding || {};

  return (
    <div data-testid="contact-page">
      <section className="py-20 lg:py-24 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p {...fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-3">Get in Touch</motion.p>
          <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }} className="font-heading text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05] tracking-tight max-w-4xl">
            Contact <span className="italic">Us</span>
          </motion.h1>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="text-slate-600 text-lg mt-5 max-w-2xl">
            We&rsquo;re here to help you with admissions, program details, and campus visits. Reach out — our team will
            respond within one working day.
          </motion.p>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <motion.aside {...fadeUp} className="lg:col-span-5 space-y-6">
            <div style={{ backgroundColor: "var(--brand-primary)" }} className="text-white rounded-3xl p-8 shadow-[0_20px_40px_rgba(30,58,138,0.25)]">
              <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: "var(--brand-secondary)" }}>Main Office</p>
              <h3 className="font-heading text-2xl leading-snug mb-6">{brand.collegeName}</h3>
              <ul className="space-y-4 text-sm">
                {contact.address && (
                  <li className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 mt-1 shrink-0" style={{ color: "var(--brand-secondary)" }} />
                    <span className="text-white/80 leading-relaxed">{contact.address}</span>
                  </li>
                )}
                {contact.officePhone && (
                  <li className="flex items-center gap-3">
                    <Phone className="w-4 h-4 shrink-0" style={{ color: "var(--brand-secondary)" }} />
                    <a href={`tel:${(contact.officePhone || "").replace(/\s/g, "")}`} data-testid="contact-office-phone" className="text-white/80 hover:text-white transition-colors">
                      {contact.officePhone}
                    </a>
                  </li>
                )}
                {contact.email && (
                  <li className="flex items-center gap-3">
                    <Mail className="w-4 h-4 shrink-0" style={{ color: "var(--brand-secondary)" }} />
                    <a href={`mailto:${contact.email}`} data-testid="contact-office-email" className="text-white/80 hover:text-white transition-colors">
                      {contact.email}
                    </a>
                  </li>
                )}
              </ul>
            </div>

            {admissionContacts.length > 0 && (
              <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-4">Admission Contacts</p>
                <ul className="divide-y divide-slate-100">
                  {admissionContacts.map((c) => (
                    <li key={c.name} data-testid={`admission-contact-${(c.role || "").toLowerCase().replace(/\s+/g, "-")}`} className="py-4 flex items-start gap-4 first:pt-0 last:pb-0">
                      <span className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ backgroundColor: "rgba(244,63,94,0.08)", color: "var(--brand-secondary)" }}>
                        <User className="w-5 h-5" />
                      </span>
                      <div className="flex-1">
                        <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{c.role}</p>
                        <p className="font-heading text-lg text-slate-900 leading-snug">{c.name}</p>
                        <a href={`tel:${(c.phone || "").replace(/\s/g, "")}`} className="text-sm transition-colors mt-1 inline-block hover:opacity-80" style={{ color: "var(--brand-primary)" }}>
                          {c.phone}
                        </a>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.aside>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} id="inquiry" className="lg:col-span-7">
            <InquiryForm />
          </motion.div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
          <div className="rounded-3xl overflow-hidden border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            <iframe
              title="Campus location"
              data-testid="contact-map"
              src="https://www.google.com/maps?q=Chokkanahalli,Jakkur+Post,+Hegdenagar,+Yelahanka+Hobli,+Bangalore+560064&output=embed"
              className="w-full h-[360px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
